import { CboCuidadoCompartilhadoSelectModel } from 'components/form/field/select/CboCuidadoCompartilhadoSelectField/CboCuidadoCompartilhadoSelectField'
import { CiapSelectFieldModel } from 'components/form/field/select/CiapSelectField/CiapSelectField'
import { CidSelectFieldModel } from 'components/form/field/select/CidSelectField/CidSelectField'
import { FlagsContextModel } from 'config/useFlagsContext'
import { FORM_ERROR } from 'final-form'
import {
  createValidator,
  empty,
  ErrorObject,
  oneRequired,
  required,
  requiredRichText,
  richTextMaxLength,
} from 'util/validation'

import { LotacoesCuidadoCompartilhadoSelectModel } from '../components/LotacoesCuidadoCompartilhadoSelectField'
import {
  CuidadoCompartilhadoPlanoModel,
  LOTACAO_COM_AGENDA_FECHADA_VALIDATOR_SOAP_MSG,
  TipoCompartilhamentoCuidadoPlanoEnum,
} from '../model-cuidadocompartilhado'
import { canRegisterCid10, getIsVinculacaoCuidadoAtiva } from './utils-cuidadoCompartilhado'

export const cuidadoCompartilhadoFormValidator = (
  unidadeSaudeId: ID,
  acessoCbo2002: string,
  flags: FlagsContextModel,
  encaminhamentos: readonly CuidadoCompartilhadoPlanoModel[],
  isAtendimentoOdonto: boolean
) => {
  const isVinculacaoCuidadoAtiva = getIsVinculacaoCuidadoAtiva(flags, isAtendimentoOdonto)
  const canUseCid10 = (cbo2002: string) => canRegisterCid10(cbo2002)

  return createValidator<CuidadoCompartilhadoPlanoModel>(
    {
      prioridade: [required],
      problemasECondicoes: [required],
      discussao: [requiredRichText],
    },
    (values: CuidadoCompartilhadoPlanoModel, errors: ErrorObject<CuidadoCompartilhadoPlanoModel>) => {
      const isGuiaEncaminhamento =
        values?.cbo && values?.tipo === TipoCompartilhamentoCuidadoPlanoEnum.GUIA_ENCAMINHAMENTO

      if (isGuiaEncaminhamento) {
        return validateGuiaEncaminhamento(values, errors, acessoCbo2002, encaminhamentos, canUseCid10)
      } else {
        return validateCuidadoCompartilhado(
          values,
          errors,
          unidadeSaudeId,
          isAtendimentoOdonto,
          acessoCbo2002,
          isVinculacaoCuidadoAtiva,
          canUseCid10
        )
      }
    }
  )
}

const validateCuidadoCompartilhado = (
  values: CuidadoCompartilhadoPlanoModel,
  errors: ErrorObject<CuidadoCompartilhadoPlanoModel>,
  unidadeSaudeId: ID,
  isAtendimentoOdonto: boolean,
  acessoCbo2002: string,
  isVinculacaoAtiva: boolean,
  canUseCid10: (cbo2002: string) => boolean
): ErrorObject<CuidadoCompartilhadoPlanoModel> => {
  errors.discussao = errors.discussao ?? richTextMaxLength(1000)(values.discussao)
  errors.cidadaoAceitaAtendTic = required(values.cidadaoAceitaAtendTic)
  errors.especialidade = empty(values.especialidade)

  validateCboAndLotacao(values, errors, unidadeSaudeId, isAtendimentoOdonto, isVinculacaoAtiva)

  if (isVinculacaoAtiva) errors.unidadeSaude = required(values.unidadeSaude)

  validateProblemasECondicoesForCuidadoCompartilhado(
    errors,
    acessoCbo2002,
    canUseCid10,
    values.problemasECondicoes?.ciap,
    values.problemasECondicoes?.cid10
  )

  return errors
}

const validateProblemasECondicoesForCuidadoCompartilhado = (
  errors: ErrorObject<CuidadoCompartilhadoPlanoModel>,
  cbo2002: string,
  canUseCid10: (cbo2002: string) => boolean,
  ciap?: CiapSelectFieldModel,
  cid10?: CidSelectFieldModel
) => {
  if (canUseCid10(cbo2002)) {
    errors.problemasECondicoes = errors.problemasECondicoes ?? oneRequired([ciap, cid10])
  } else {
    errors.problemasECondicoes = errors.problemasECondicoes ?? {
      ciap: required(ciap),
      cid10: empty(cid10),
    }
  }
}

const validateGuiaEncaminhamento = (
  values: CuidadoCompartilhadoPlanoModel,
  errors: ErrorObject<CuidadoCompartilhadoPlanoModel>,
  acessoCbo2002: string,
  encaminhamentos: readonly CuidadoCompartilhadoPlanoModel[],
  canUseCid10: (cbo2002: string) => boolean
): ErrorObject<CuidadoCompartilhadoPlanoModel> => {
  errors.cbo = required(values.cbo)
  errors.discussao = errors.discussao ?? richTextMaxLength(2000)(values.discussao)
  errors.lotacao = empty(values.lotacao)
  errors.unidadeSaude = empty(values.unidadeSaude)
  errors.cidadaoAceitaAtendTic = empty(values.cidadaoAceitaAtendTic)
  errors.especialidade = required(values.especialidade)

  isEncaminhamentoDuplicado(encaminhamentos, values, errors)

  validateProblemasECondicoesForGuiaEncaminhamento(
    errors,
    acessoCbo2002,
    canUseCid10,
    values.problemasECondicoes?.ciap,
    values.problemasECondicoes?.cid10
  )

  return errors
}

const validateProblemasECondicoesForGuiaEncaminhamento = (
  errors: ErrorObject<CuidadoCompartilhadoPlanoModel>,
  cbo2002: string,
  canUseCid10: (cbo2002: string) => boolean,
  ciap?: CiapSelectFieldModel,
  cid10?: CidSelectFieldModel
) => {
  if (canUseCid10(cbo2002)) {
    errors.problemasECondicoes = errors.problemasECondicoes ?? {
      cid10: required(cid10),
    }
  } else {
    errors.problemasECondicoes = errors.problemasECondicoes ?? {
      ciap: required(ciap),
      cid10: required(cid10),
    }
  }
}

const isEncaminhamentoDuplicado = (
  encaminhamentos: readonly CuidadoCompartilhadoPlanoModel[],
  values: CuidadoCompartilhadoPlanoModel,
  errors: ErrorObject<CuidadoCompartilhadoPlanoModel>
) => {
  return encaminhamentos.some((encaminhamento) => {
    if (
      encaminhamento._id !== values._id &&
      encaminhamento.cbo.cbo.id === values.cbo.cbo.id &&
      encaminhamento.problemasECondicoes.cid10?.id === values.problemasECondicoes?.cid10?.id
    ) {
      errors[FORM_ERROR] = 'Já existe uma Guia de encaminhamento cadastrada para este CBO e Problemas/condições (CID10)'
      return true
    }
    return false
  })
}

const validateLotacaoComAgendaFechada = (hasAgendaFechada: boolean | undefined): string | undefined =>
  hasAgendaFechada ? LOTACAO_COM_AGENDA_FECHADA_VALIDATOR_SOAP_MSG : undefined

const validateCboAndLotacao = (
  values: CuidadoCompartilhadoPlanoModel,
  errors: ErrorObject<CuidadoCompartilhadoPlanoModel>,
  unidadeSaudeId: ID,
  isAtendimentoOdonto: boolean,
  isVinculacaoAtiva: boolean
) => {
  const isEMulti = unidadeSaudeId === values.unidadeSaude?.id

  if (isVinculacaoAtiva) {
    validateCboAndLotacaoFlagAtiva(values.lotacao, values.cbo, errors, isEMulti)
  } else {
    validateCboAndLotacaoFlagDesativada(values.lotacao, values.cbo, errors, isAtendimentoOdonto, isEMulti)
  }
}

const validateCboAndLotacaoFlagAtiva = (
  lotacao: LotacoesCuidadoCompartilhadoSelectModel | undefined,
  cbo: CboCuidadoCompartilhadoSelectModel,
  errors: ErrorObject<CuidadoCompartilhadoPlanoModel>,
  isEMulti: boolean
) => {
  if (isEMulti) {
    errors.lotacao = required(lotacao)
  } else {
    errors.cbo = required(cbo)
  }
  errors.lotacao = errors.lotacao ?? validateLotacaoComAgendaFechada(lotacao?.hasAgendaFechada)
}

const validateCboAndLotacaoFlagDesativada = (
  lotacao: LotacoesCuidadoCompartilhadoSelectModel | undefined,
  cbo: CboCuidadoCompartilhadoSelectModel,
  errors: ErrorObject<CuidadoCompartilhadoPlanoModel>,
  isAtendimentoOdonto: boolean,
  isEMulti: boolean
) => {
  if (isEMulti || isAtendimentoOdonto) {
    errors.lotacao = required(lotacao)
  } else errors.cbo = required(cbo)
}
